import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { LifeBuoy, Paperclip, X, CheckCircle2, ArrowRight, ArrowLeft, ImagePlus, ListChecks, HelpCircle, Bug, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../../api';

const ACCENT = '#0F5F6B';

const IMPACT_OPTIONS = [
    { value: 'cannot_continue', label: 'I cannot continue my work', hint: 'The system is down or blocking me completely.' },
    { value: 'major_delay',     label: 'It is causing a major delay', hint: 'A key process is affected and slowing things down a lot.' },
    { value: 'workaround',      label: 'I can continue using a workaround', hint: "There's a manual way around it for now." },
    { value: 'minor',           label: 'It is a minor issue or improvement', hint: 'Cosmetic, or a nice-to-have change.' },
];

const REQUEST_TYPES = [
    { v: 'question', l: 'Ask a Question', icon: HelpCircle, d: 'A general query — no urgency or reproduction steps needed, just tell us what you\'d like to know.' },
    { v: 'issue', l: 'Report an Issue', icon: Bug, d: 'Something is not working correctly, or is preventing you from completing part of your work.' },
    { v: 'improvement', l: 'Request Something', icon: Sparkles, d: 'You would like a new feature, or an existing one improved, changed or made easier to use.' },
];

function detectEnvironment() {
    const ua = navigator.userAgent || '';
    let browser = 'Unknown browser';
    if (/Edg\//.test(ua)) browser = 'Edge';
    else if (/OPR\//.test(ua)) browser = 'Opera';
    else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) browser = 'Chrome';
    else if (/Firefox\//.test(ua)) browser = 'Firefox';
    else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';
    let os = 'Unknown OS';
    if (/Windows/.test(ua)) os = 'Windows';
    else if (/Mac OS X/.test(ua)) os = 'macOS';
    else if (/Android/.test(ua)) os = 'Android';
    else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
    else if (/Linux/.test(ua)) os = 'Linux';
    const device_type = /iPad|Tablet/.test(ua) ? 'Tablet' : (/Mobi|Android|iPhone/.test(ua) ? 'Mobile' : 'Desktop');
    const screen_size = (window.screen && window.screen.width) ? `${window.screen.width}x${window.screen.height}` : null;
    return { browser, os, device_type, screen_size };
}

function isValidEmail(v) { return /\S+@\S+\.\S+/.test(v || ''); }

export default function GeneralTicketForm() {
    const [searchParams] = useSearchParams();
    const fromPageUrl = searchParams.get('from');
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [files, setFiles] = useState([]);
    const [step, setStep] = useState(0);
    const dropRef = useRef(null);

    const [form, setForm] = useState({
        request_type: 'question',
        title: '',
        description: '',
        location_area: '',
        what_happened: '',
        what_expected: '',
        impact: '',
        name: '',
        email: '',
        company_name: '',
        extra_info: '',
    });
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const isIssue = form.request_type === 'issue';
    const isQuestion = form.request_type === 'question';

    // Question flow is deliberately short: just the box, then who-you-are.
    // Issue/improvement keep the full guided flow.
    const STEPS = isQuestion
        ? [{ key: 'whats_happening', label: 'Your question' }, { key: 'who_are_you', label: 'Who are you' }]
        : [{ key: 'whats_happening', label: "What's happening" }, { key: 'how_urgent', label: 'How urgent' }, { key: 'who_are_you', label: 'Who are you' }, { key: 'anything_else', label: 'Anything to add' }];
    const lastStepIdx = STEPS.length - 1;

    const addFiles = useCallback((list) => {
        const arr = Array.from(list || []).filter(f => f.size <= 15 * 1024 * 1024);
        if (arr.length !== (list || []).length) toast.error('Some files exceed the 15MB limit and were skipped.');
        if (arr.length) setFiles(prev => [...prev, ...arr]);
    }, []);

    useEffect(() => {
        const handler = (e) => {
            const items = e.clipboardData?.items || [];
            const pasted = [];
            for (const it of items) {
                if (it.type?.startsWith('image/')) {
                    const f = it.getAsFile();
                    if (f) pasted.push(new File([f], f.name || `pasted-screenshot-${Date.now()}.png`, { type: f.type }));
                }
            }
            if (pasted.length) { addFiles(pasted); toast.message('Screenshot pasted'); if (!isQuestion) setStep(lastStepIdx); }
        };
        window.addEventListener('paste', handler);
        return () => window.removeEventListener('paste', handler);
    }, [addFiles, isQuestion, lastStepIdx]);

    const stepValid = () => {
        const key = STEPS[step].key;
        if (key === 'whats_happening') {
            if (!form.title.trim()) return false;
            if (isIssue) return !!(form.what_happened.trim() && form.what_expected.trim());
            return !!form.description.trim();
        }
        if (key === 'how_urgent') return !!form.impact;
        if (key === 'who_are_you') return isValidEmail(form.email) && !!form.name.trim() && !!form.company_name.trim();
        return true;
    };

    const next = () => {
        if (!stepValid()) {
            const msgs = {
                whats_happening: isIssue ? 'Please fill in the title and answer both questions below.' : (isQuestion ? 'Please fill in the title and your question.' : 'Please fill in the title and description.'),
                how_urgent: 'Please tell us how this is affecting your work.',
                who_are_you: 'Please fill in your name, email and company name.',
            };
            return toast.error(msgs[STEPS[step].key] || 'Please complete this step.');
        }
        setStep(s => Math.min(s + 1, lastStepIdx));
    };
    const back = () => setStep(s => Math.max(s - 1, 0));

    const submit = async () => {
        setSubmitting(true);
        try {
            const env = detectEnvironment();
            const payload = {
                request_type: form.request_type,
                title: form.title.trim(),
                extra_info: form.extra_info || null,
                page_url: fromPageUrl || document.referrer || null,
                browser: env.browser, os: env.os, device_type: env.device_type, screen_size: env.screen_size,
                submitted_by_name: form.name.trim(),
                submitted_by_email: form.email.trim(),
                submitted_by_org: form.company_name.trim(),
            };
            if (isIssue) {
                payload.location_area = form.location_area.trim() || null;
                payload.what_happened = form.what_happened.trim();
                payload.what_expected = form.what_expected.trim();
                payload.impact = form.impact;
            } else if (isQuestion) {
                payload.description = form.description.trim();
            } else {
                payload.location_area = form.location_area.trim() || null;
                payload.description = form.description.trim();
                payload.impact = form.impact;
            }
            const r = await api.post('/public/support/tickets', payload);
            if (files.length) {
                const fd = new FormData();
                files.forEach(f => fd.append('files', f));
                await api.post(`/public/support/tickets/${r.data.public_token}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            }
            setResult(r.data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Submission failed');
        } finally { setSubmitting(false); }
    };

    if (result) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="general-ticket-confirmation">
                <div className="bg-white rounded-xl border border-emerald-200 p-8 max-w-md text-center shadow-sm">
                    <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
                    <h1 className="mt-3 text-[18px] font-bold text-slate-900">Thank you — your request has been submitted</h1>
                    <p className="mt-2 text-[13.5px] text-slate-600">
                        We will review it and get back to you by email. Your reference is:
                    </p>
                    <div className="mt-3 inline-block bg-slate-100 rounded-lg px-4 py-2 font-mono font-semibold" style={{ color: ACCENT }} data-testid="general-ticket-reference">
                        {result.ticket_number}
                    </div>
                    <div className="mt-5 flex items-center justify-center gap-4">
                        <Link to={`/support/${result.public_token}`} className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold hover:underline" style={{ color: ACCENT }} data-testid="general-ticket-view-request-link">
                            View your request <ArrowRight size={14} />
                        </Link>
                        <Link to="/support/my-requests" className="inline-flex items-center gap-1.5 text-[13px] text-slate-400 hover:underline" data-testid="general-ticket-view-all-requests-link">
                            <ListChecks size={13} /> See all your requests
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const stepKey = STEPS[step].key;

    return (
        <div className="min-h-screen bg-slate-50" data-testid="general-ticket-form">
            <header className="text-white px-6 py-5" style={{ background: ACCENT }}>
                <div className="max-w-2xl mx-auto flex items-center gap-2">
                    <LifeBuoy className="w-5 h-5 text-white/80" />
                    <div>
                        <div className="text-[11px] uppercase tracking-wide text-white/60">Beyond Measure Support</div>
                        <div className="text-[16px] font-semibold">Ask a Question, Report an Issue, or Request Something</div>
                    </div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <div className="flex items-center gap-1.5 mb-5" data-testid="general-ticket-step-progress">
                    {STEPS.map((s, i) => (
                        <React.Fragment key={s.key}>
                            <div className={`flex items-center gap-1.5 ${i <= step ? '' : 'opacity-40'}`}>
                                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0"
                                     style={{ background: i <= step ? ACCENT : '#cbd5e1' }}>{i + 1}</div>
                                <span className="text-[11.5px] font-medium text-slate-700 hidden sm:inline">{s.label}</span>
                            </div>
                            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
                        </React.Fragment>
                    ))}
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 sm:p-6 space-y-5">
                    {stepKey === 'whats_happening' && (
                        <>
                            <div>
                                <label className="text-[13px] font-semibold text-slate-800">What would you like to do? <span className="text-rose-500">*</span></label>
                                <div className="mt-2 grid grid-cols-1 gap-2">
                                    {REQUEST_TYPES.map(opt => {
                                        const Icon = opt.icon;
                                        return (
                                            <button type="button" key={opt.v} onClick={() => { set('request_type', opt.v); setStep(0); }}
                                                    data-testid={`general-type-${opt.v}`}
                                                    className={`text-left rounded-lg border p-3 transition flex items-start gap-2.5 ${form.request_type === opt.v ? 'bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}
                                                    style={form.request_type === opt.v ? { borderColor: ACCENT } : {}}>
                                                <Icon size={16} className="mt-0.5 shrink-0" style={{ color: ACCENT }} />
                                                <div>
                                                    <div className="text-[13.5px] font-semibold text-slate-800">{opt.l}</div>
                                                    <div className="text-[11.5px] text-slate-500 mt-0.5">{opt.d}</div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <label className="text-[13px] font-semibold text-slate-800">{isQuestion ? 'Your question, in short' : 'Title'} <span className="text-rose-500">*</span></label>
                                <input value={form.title} onChange={e => set('title', e.target.value)}
                                       placeholder={isQuestion ? 'e.g. Do you offer ongoing support after launch?' : 'Short summary of the problem or request'} maxLength={200}
                                       className="mt-1.5 w-full h-11 rounded-md border border-slate-300 px-3 text-[13.5px]"
                                       data-testid="general-title-input" />
                            </div>

                            {isQuestion && (
                                <div>
                                    <label className="text-[13px] font-semibold text-slate-800">Your question <span className="text-rose-500">*</span></label>
                                    <textarea value={form.description} onChange={e => set('description', e.target.value)}
                                              placeholder="Tell us what you'd like to know…" rows={5}
                                              className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-[13.5px]"
                                              data-testid="general-question-input" />
                                </div>
                            )}

                            {isIssue && (
                                <>
                                    <div>
                                        <label className="text-[13px] font-semibold text-slate-800">Which product or platform is this about? <span className="text-slate-400 font-normal">(optional)</span></label>
                                        <input value={form.location_area} onChange={e => set('location_area', e.target.value)}
                                               placeholder="e.g. our website, a specific app you use…" maxLength={200}
                                               className="mt-1.5 w-full h-11 rounded-md border border-slate-300 px-3 text-[13.5px]"
                                               data-testid="general-location-input" />
                                    </div>
                                    <div>
                                        <label className="text-[13px] font-semibold text-slate-800">What were you doing, and what happened? <span className="text-rose-500">*</span></label>
                                        <textarea value={form.what_happened} onChange={e => set('what_happened', e.target.value)}
                                                  placeholder="e.g. I was trying to submit a form, and got an error message…" rows={3}
                                                  className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-[13.5px]"
                                                  data-testid="general-happened-input" />
                                    </div>
                                    <div>
                                        <label className="text-[13px] font-semibold text-slate-800">What did you expect to happen? <span className="text-rose-500">*</span></label>
                                        <textarea value={form.what_expected} onChange={e => set('what_expected', e.target.value)}
                                                  placeholder="e.g. The form should have submitted successfully…" rows={2}
                                                  className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-[13.5px]"
                                                  data-testid="general-expected-input" />
                                    </div>
                                </>
                            )}

                            {!isIssue && !isQuestion && (
                                <>
                                    <div>
                                        <label className="text-[13px] font-semibold text-slate-800">Which product or platform does this relate to? <span className="text-slate-400 font-normal">(optional)</span></label>
                                        <input value={form.location_area} onChange={e => set('location_area', e.target.value)}
                                               placeholder="e.g. our website, a specific app you use…" maxLength={200}
                                               className="mt-1.5 w-full h-11 rounded-md border border-slate-300 px-3 text-[13.5px]"
                                               data-testid="general-location-input" />
                                    </div>
                                    <div>
                                        <label className="text-[13px] font-semibold text-slate-800">Tell us what you would like <span className="text-rose-500">*</span></label>
                                        <textarea value={form.description} onChange={e => set('description', e.target.value)}
                                                  placeholder="What would you like built, changed or improved, and why would this help you?" rows={5}
                                                  className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-[13.5px]"
                                                  data-testid="general-description-input" />
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {stepKey === 'how_urgent' && (
                        <div>
                            <label className="text-[13px] font-semibold text-slate-800">How is this affecting your work? <span className="text-rose-500">*</span></label>
                            <div className="mt-2 space-y-2">
                                {IMPACT_OPTIONS.map(opt => (
                                    <label key={opt.value} className={`flex items-start gap-2.5 rounded-lg border p-3 cursor-pointer transition ${form.impact === opt.value ? 'bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}
                                           style={form.impact === opt.value ? { borderColor: ACCENT } : {}}
                                           data-testid={`general-impact-${opt.value}`}>
                                        <input type="radio" name="impact" className="mt-0.5" checked={form.impact === opt.value} onChange={() => set('impact', opt.value)} />
                                        <div>
                                            <div className="text-[13px] font-medium text-slate-800">{opt.label}</div>
                                            <div className="text-[11px] text-slate-500">{opt.hint}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {stepKey === 'who_are_you' && (
                        <div className="space-y-3">
                            <div>
                                <label className="text-[13px] font-semibold text-slate-800">Your name <span className="text-rose-500">*</span></label>
                                <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jane Smith"
                                       className="mt-1.5 w-full h-11 rounded-md border border-slate-300 px-3 text-[13.5px]" data-testid="general-name-input" />
                            </div>
                            <div>
                                <label className="text-[13px] font-semibold text-slate-800">Company name <span className="text-rose-500">*</span></label>
                                <input value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="Your company"
                                       className="mt-1.5 w-full h-11 rounded-md border border-slate-300 px-3 text-[13.5px]" data-testid="general-company-input" />
                            </div>
                            <div>
                                <label className="text-[13px] font-semibold text-slate-800">Your email <span className="text-rose-500">*</span></label>
                                <input value={form.email} type="email" onChange={e => set('email', e.target.value)} placeholder="you@example.com"
                                       className="mt-1.5 w-full h-11 rounded-md border border-slate-300 px-3 text-[13.5px]" data-testid="general-email-input" />
                            </div>
                        </div>
                    )}

                    {stepKey === 'anything_else' && (
                        <>
                            <div>
                                <label className="text-[13px] font-semibold text-slate-800">Screenshots or attachments <span className="text-slate-400 font-normal">(optional)</span></label>
                                <div
                                    ref={dropRef}
                                    onDragOver={e => { e.preventDefault(); }}
                                    onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                                    onClick={() => document.getElementById('general-file-input').click()}
                                    className="mt-1.5 border-2 border-dashed border-slate-300 rounded-lg p-5 text-center cursor-pointer hover:border-slate-400 transition"
                                    data-testid="general-dropzone"
                                >
                                    <ImagePlus className="w-5 h-5 mx-auto text-slate-400" />
                                    <div className="text-[11.5px] text-slate-500 mt-1">Tap to add a photo, or drag & drop a file here.</div>
                                    <div className="text-[10.5px] text-slate-400 mt-1">Or press <strong>Ctrl+V</strong> (or <strong>Cmd+V</strong>) anywhere on this page to paste a screenshot.</div>
                                    <input id="general-file-input" type="file" multiple accept="image/*,.pdf" className="hidden" onChange={e => addFiles(e.target.files)} data-testid="general-file-input" />
                                </div>
                                {files.length > 0 && (
                                    <div className="mt-2 space-y-1">
                                        {files.map((f, i) => (
                                            <div key={i} className="flex items-center justify-between text-[12px] bg-slate-50 rounded px-2.5 py-1.5" data-testid={`general-file-${i}`}>
                                                <span className="flex items-center gap-1.5 text-slate-700 truncate"><Paperclip size={12} />{f.name}</span>
                                                <button type="button" onClick={() => setFiles(files.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-500"><X size={13} /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="text-[13px] font-semibold text-slate-800">Anything else we should know? <span className="text-slate-400 font-normal">(optional)</span></label>
                                <textarea value={form.extra_info} onChange={e => set('extra_info', e.target.value)}
                                          placeholder="Deadlines, workarounds, number of users affected, additional context…" rows={2}
                                          className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-[13.5px]"
                                          data-testid="general-extra-info" />
                            </div>
                            <div className="text-[11px] text-slate-500 bg-slate-50 rounded-md p-2.5">
                                Critical and high-impact issues will be reviewed as soon as possible. We'll keep you updated on where things stand via the tracking link we send you.
                            </div>
                        </>
                    )}

                    <div className="flex items-center gap-2 pt-1">
                        {step > 0 && (
                            <button type="button" onClick={back} className="h-11 px-4 rounded-md font-semibold text-[13.5px] border border-slate-300 text-slate-600 flex items-center gap-1.5" data-testid="general-back-btn">
                                <ArrowLeft size={14} /> Back
                            </button>
                        )}
                        {step < lastStepIdx ? (
                            <button type="button" onClick={next}
                                    className="flex-1 h-11 rounded-md font-semibold text-white text-[14px] flex items-center justify-center gap-1.5"
                                    style={{ background: ACCENT }} data-testid="general-next-btn">
                                Continue <ArrowRight size={14} />
                            </button>
                        ) : (
                            <button type="button" onClick={() => { if (!stepValid()) { return toast.error('Please fill in your name, email and company name.'); } submit(); }} disabled={submitting}
                                    className="flex-1 h-11 rounded-md font-semibold text-white text-[14px] disabled:opacity-60"
                                    style={{ background: ACCENT }} data-testid="general-submit-btn">
                                {submitting ? 'Submitting…' : 'Submit request'}
                            </button>
                        )}
                    </div>
                </div>
            </main>

            <footer className="max-w-2xl mx-auto px-4 sm:px-6 pb-8 pt-2 text-center" data-testid="general-ticket-footer">
                <p className="text-[11px] leading-relaxed text-slate-400">
                    © 2026 Beyond Measure. All rights reserved.<br />
                    Beyond Measure is a trading name of Beyond Ops Ltd, a company registered in England and Wales.<br />
                    Company No. 17276143.<br />
                    Registered Office: 71–75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom.
                </p>
            </footer>
        </div>
    );
}
